import React, { useCallback } from 'react'
import dayjs from 'dayjs'
import AsyncSearchSelect, { SearchOption } from '@/components/common/AsyncSearchSelect'
import DateTimePicker from '@/components/common/DateTimePicker'
import { TripSegment } from '@/types/forms'
import { apiClient } from '@/services/api'

interface TripSegmentListProps {
  segments: TripSegment[]
  onChange: (segments: TripSegment[]) => void
  disabled?: boolean  // 🔑 新增：是否禁用（查看模式）
}

/**
 * 多段行程列表组件
 * 支持：添加、删除、编辑行程段，自动序号重排
 */
const TripSegmentList: React.FC<TripSegmentListProps> = ({
  segments,
  onChange,
  disabled = false,  // 🔑 默认不禁用
}) => {
  // 城市搜索处理函数
  const handleCitySearch = useCallback(
    async (keyword: string) => {
      return await apiClient.searchCities(keyword)
    },
    []
  )

  // 城市选项的自定义渲染格式
  const formatCityOption = (city: SearchOption) => (
    <div className="text-sm font-semibold text-gray-900">
      {city.name}
    </div>
  )

  // 添加新行程段
  const handleAddSegment = () => {
    const newSegment: TripSegment = {
      sequence: segments.length + 1,
      startDate: '',
      endDate: '',
      departureCity: '',
      departureCityCode: '',
      arrivalCity: '',
      arrivalCityCode: '',
      purpose: '',
    }
    const updated = [...segments, newSegment]
    onChange(updated)
  }

  // 删除行程段并重排序号
  const handleDeleteSegment = (index: number) => {
    const updated = segments
      .filter((_, i) => i !== index)
      .map((seg, i) => ({
        ...seg,
        sequence: i + 1,
      }))
    onChange(updated)
  }

  // 更新单个行程段字段；startDate 变化时若 endDate 失效则自动清空
  const handleSegmentChange = (
    index: number,
    field: keyof TripSegment,
    value: string
  ) => {
    const updated = segments.map((seg, i) => {
      if (i !== index) return seg
      const next = { ...seg, [field]: value }
      if (field === 'startDate' && value && next.endDate && dayjs(next.endDate).isBefore(dayjs(value))) {
        next.endDate = ''
      }
      return next
    })
    onChange(updated)
  }

  // 更新行程段城市（同时更新名称和编码）
  const handleSegmentCityChange = (
    index: number,
    cityField: 'departureCity' | 'arrivalCity',
    codeField: 'departureCityCode' | 'arrivalCityCode',
    label: string,
    option?: SearchOption
  ) => {
    const city = option as import('@/types').CityDTO
    const updated = segments.map((seg, i) =>
      i === index
        ? { ...seg, [cityField]: city?.name || label, [codeField]: city?.code || '' }
        : seg
    )
    onChange(updated)
  }

  return (
    <div className="space-y-clay-md">
      {segments.length === 0 ? (
        <div className="p-clay-lg rounded-clay bg-gray-50 border border-gray-200 text-center">
          <p className="text-clay-sm text-gray-600">
            暂无行程段，请点击"添加行程段"开始添加
          </p>
        </div>
      ) : (
        <div className="space-y-clay-md">
          {/* PC 端：紧凑表格式布局（lg 及以上屏幕） */}
          <div className="hidden lg:block w-full">
            <table className="w-full text-clay-sm border-collapse rounded-clay border border-slate-200">
                <thead className="bg-gray-100 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 w-12">序号</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 min-w-[120px]">开始日期</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 min-w-[120px]">结束日期</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 min-w-[110px]">出发城市</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 min-w-[110px]">到达城市</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 min-w-[120px]">本段事由</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700 w-12">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {segments.map((segment, index) => (
                    <tr
                      key={`segment-${index}`}
                      className="hover:bg-gray-50 transition-colors"
                      style={{
                        position: 'relative',
                        zIndex: 100 + (segments.length - index)
                      }}
                    >
                      <td className="px-4 py-3 align-middle">
                        <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">
                          {segment.sequence}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <DateTimePicker
                          label=""
                          value={segment.startDate}
                          onChange={(value) =>
                            handleSegmentChange(index, 'startDate', value)
                          }
                          placeholder="选择开始日期"
                          required={false}
                          compact={true}
                          disabled={disabled}
                        />
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <DateTimePicker
                          label=""
                          value={segment.endDate}
                          onChange={(value) =>
                            handleSegmentChange(index, 'endDate', value)
                          }
                          placeholder="选择结束日期"
                          required={false}
                          compact={true}
                          disabled={disabled}
                          minDate={segment.startDate || undefined}
                          defaultTime={[18, 30]}
                        />
                      </td>
                      <td className="px-4 py-3 align-middle" style={{ position: 'relative' }}>
                        <div style={{ position: 'relative', zIndex: 9999 }}>
                          <AsyncSearchSelect
                            label=""
                            value={segment.departureCity || ''}
                            displayText={segment.departureCity || ''}
                            onSearch={handleCitySearch}
                            onChange={(_, label, option) => {
                              handleSegmentCityChange(index, 'departureCity', 'departureCityCode', label, option)
                            }}
                            loadDefaultOptions={() => apiClient.getHotCities()}
                            formatOption={formatCityOption}
                            placeholder="请输入出发城市"
                            className="text-clay-xs segment-select"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle" style={{ position: 'relative' }}>
                        <div style={{ position: 'relative', zIndex: 9999 }}>
                          <AsyncSearchSelect
                            label=""
                            value={segment.arrivalCity || ''}
                            displayText={segment.arrivalCity || ''}
                            onSearch={handleCitySearch}
                            onChange={(_, label, option) => {
                              handleSegmentCityChange(index, 'arrivalCity', 'arrivalCityCode', label, option)
                            }}
                            loadDefaultOptions={() => apiClient.getHotCities()}
                            formatOption={formatCityOption}
                            placeholder="请输入到达城市"
                            className="text-clay-xs segment-select"
                          />
                          {segment.departureCity && segment.arrivalCity &&
                            segment.departureCity === segment.arrivalCity && (
                            <p className="text-xs text-red-500 mt-0.5 whitespace-nowrap">
                              出发与到达城市不能相同
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="出差事由"
                            className="clay-input text-clay-xs w-full pr-12"
                            style={{ height: '36px', minHeight: '36px' }}
                            maxLength={200}
                            value={segment.purpose}
                            onChange={(e) =>
                              handleSegmentChange(index, 'purpose', e.target.value)
                            }
                            required
                          />
                          <span className="absolute right-2 bottom-1.5 text-[10px] text-slate-400 pointer-events-none leading-none">
                            {segment.purpose?.length || 0}/200
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {!disabled && (  // 🔑 查看模式隐藏删除按钮
                          <button
                            type="button"
                            onClick={() => handleDeleteSegment(index)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors inline-block"
                            title="删除此行程段"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </div>

          {/* 平板和移动端：卡片式布局（lg 以下屏幕） */}
          <div className="lg:hidden space-y-clay-md">
            {segments.map((segment, index) => (
              <div
                key={`segment-${index}`}
                className="p-clay-md rounded-clay border border-slate-200 bg-white hover:shadow-clay transition-shadow"
              >
                {/* 行程段头部 - 序号和删除按钮 */}
                <div className="flex items-center justify-between mb-clay-md">
                  <div className="flex items-center gap-clay-md">
                    <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-clay-sm font-bold">
                      {segment.sequence}
                    </div>
                    <span className="text-clay-sm font-medium text-gray-600">
                      行程段 {segment.sequence}
                    </span>
                  </div>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => handleDeleteSegment(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="删除此行程段"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  )}
                </div>

                {/* 行程段内容 - 网格布局 */}
                <div className="grid grid-cols-1 gap-clay-md">
                  {/* 开始日期 */}
                  <div>
                    <DateTimePicker
                      label="开始日期"
                      value={segment.startDate}
                      onChange={(value) =>
                        handleSegmentChange(index, 'startDate', value)
                      }
                      placeholder="请选择开始日期和时间"
                      required={true}
                      disabled={disabled}
                    />
                  </div>

                  {/* 结束日期 */}
                  <div>
                    <DateTimePicker
                      label="结束日期"
                      value={segment.endDate}
                      onChange={(value) =>
                        handleSegmentChange(index, 'endDate', value)
                      }
                      placeholder="请选择结束日期和时间"
                      required={true}
                      minDate={segment.startDate || undefined}
                      defaultTime={[18, 30]}
                      disabled={disabled}
                    />
                  </div>

                  {/* 出发城市 */}
                  <div>
                    <AsyncSearchSelect
                      label="出发城市"
                      value={segment.departureCity || ''}
                      displayText={segment.departureCity || ''}
                      onSearch={handleCitySearch}
                      onChange={(_, label, option) => {
                        handleSegmentCityChange(index, 'departureCity', 'departureCityCode', label, option)
                      }}
                      loadDefaultOptions={() => apiClient.getHotCities()}
                      formatOption={formatCityOption}
                      placeholder="请输入出发城市"
                      required={true}
                      disabled={disabled}
                    />
                  </div>

                  {/* 到达城市 */}
                  <div>
                    <AsyncSearchSelect
                      label="到达城市"
                      value={segment.arrivalCity || ''}
                      displayText={segment.arrivalCity || ''}
                      onSearch={handleCitySearch}
                      onChange={(_, label, option) => {
                        handleSegmentCityChange(index, 'arrivalCity', 'arrivalCityCode', label, option)
                      }}
                      loadDefaultOptions={() => apiClient.getHotCities()}
                      formatOption={formatCityOption}
                      placeholder="请输入到达城市"
                      required={true}
                      disabled={disabled}
                    />
                    {segment.departureCity && segment.arrivalCity &&
                      segment.departureCity === segment.arrivalCity && (
                      <p className="text-xs text-red-500 mt-1">
                        出发城市与到达城市不能相同，请重新选择
                      </p>
                    )}
                  </div>

                  {/* 事由 */}
                  <div>
                    <label className="clay-label text-clay-sm">本段事由 <span className="text-red-500">*</span></label>
                    <textarea
                      placeholder="请说明本段的出差目的和主要工作"
                      className="clay-textarea text-clay-sm w-full"
                      rows={2}
                      maxLength={200}
                      value={segment.purpose}
                      onChange={(e) =>
                        handleSegmentChange(index, 'purpose', e.target.value)
                      }
                      disabled={disabled}
                      readOnly={disabled}
                    />
                    <p className="text-xs text-slate-400 text-right mt-1">
                      {segment.purpose?.length || 0}/200 字符
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 添加行程段按钮 */}
      {!disabled && (  // 🔑 查看模式隐藏添加按钮
        <button
          type="button"
          onClick={handleAddSegment}
          className="w-full lg:w-auto py-2 px-4 rounded-clay text-clay-sm font-medium text-blue-600 hover:bg-blue-50 border border-blue-300 border-dashed transition-colors"
        >
          + 添加行程段
        </button>
      )}
    </div>
  )
}

export default TripSegmentList
